import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import styles from './GeneralContractInfoSubmitForm.module.scss'
import { useForm, Controller } from 'react-hook-form'
import ToggleButtonGroupController from '../../../components/ToggleButtonGroup/ToggleButtonGroupController'
import SimpleInputController from '../../../components/simpleInput/SimpleInputController'
import IncentiveModal from '../../onboarding/IncentiveModal'
import { formatIncentiveTaxOptionLabel } from '../../../utils/incentiveDisplay'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { Tooltip } from 'react-tooltip'

const GeneralContractInfoSubmitForm = forwardRef(
	({ onValidationChange, isOverseas, isReNegotiation = false }, ref) => {
		const { control, handleSubmit, watch, setValue, formState, trigger } = useForm({
			mode: 'onChange',
			defaultValues: {
				general: {
					applyTax: 'exclude', // 세전 (왼쪽 버튼)
					payPeriod: 'oneTime', // 임금 지급 주기 고정: 일시불
					periodSalary: '', // 기간급여액
					sponsorCap: true, // 착용 (왼쪽 버튼)
					overseasTour: false, // 해외 대회 포함 여부 (왼쪽 버튼)
					overseasSupport: 0, // 해외대회 지원 항목
					incentiveSettings: [], // 인센티브 설정
					incentiveTaxOption: 'amountDisplayed', // 인센티브 택스 옵션
				},
			},
		})

		const formatNumber = (value) => {
			if (!value) return ''
			return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
		}

		useEffect(() => {
			let previousData
			if (isReNegotiation) {
				previousData = JSON.parse(
					sessionStorage?.getItem('dataForReNegotiation'),
				)?.termContract
				console.log('previousData: ', previousData)
			}
			if (previousData) {
				const parsedData = previousData
				console.log('parsedData: ', parsedData)
				setValue(
					'general.applyTax',
					parsedData?.isSalaryTaxIncluded ? 'include' : 'exclude',
				)
				setValue('general.payPeriod', 'oneTime')
				setValue('general.periodSalary', formatNumber(parsedData?.salaryAmountForPeriod))
				setValue('general.sponsorCap', parsedData?.wearSponsoredHat)
				setValue('general.overseasTour', parsedData?.isOverseasIncluded)
				// setValue(
				// 	'domestic.jejudoSupport',
				// 	formatNumber(parsedData.expenseReimbursementJeju),
				// )
				setValue('general.overseasSupport', parsedData?.expenseReimbursement)
				// 드롭다운 선택 상태 업데이트
				setValue(
					'general.incentiveSettings',
					parsedData?.incentives
						.sort((a, b) => a.startRank - b.startRank)
						.map((setting, index) => {
							return {
								id: index + 1,
								start: setting.startRank,
								percentage: setting.percentage,
								end: setting.endRank === 0 ? 'CUT' : setting.endRank,
							}
						}),
				)
				setValue(
					'general.incentiveTaxOption',
					parsedData?.prizeType === 'displayed' ? 'amountDisplayed' : 'amountReceived',
				)

				console.log('폼 필드 업데이트 완료')

				// 유효성 검사 트리거
				setTimeout(() => {
					trigger()
				}, 100)
			}
		}, [setValue, trigger])

		useEffect(() => {
			if (isOverseas) {
				setValue('general.overseasTour', true)
			}
		}, [isOverseas])
		const [isModalOpen, setIsModalOpen] = useState(false)

		// 토글 옵션들
		const applyTaxOption = [
			{ value: 'exclude', label: '세전' },
			{ value: 'include', label: '세후' },
		]

		const sponsorCapOption = [
			{ value: true, label: '착용' },
			{ value: false, label: '미착용' },
		]

		const overseasTourOption = [
			{ value: false, label: '미포함(국내만)' },
			{ value: true, label: '해외대회 포함' },
		]

		// isOverseas가 true일 때는 해외대회 포함만 보이도록 필터링
		const filteredOverseasTourOption = isOverseas
			? overseasTourOption.filter((option) => option.value === true)
			: overseasTourOption

		// 현재 폼 데이터
		const activeData = watch('general')
		const incentiveSettings = watch('general.incentiveSettings')

		// validation 상태를 상위 컴포넌트로 전달
		useEffect(() => {
			const isValid = formState.isValid && incentiveSettings && incentiveSettings.length > 0

			onValidationChange && onValidationChange('general', isValid)
		}, [formState.isValid, incentiveSettings, onValidationChange])

		// 가격 입력 처리 함수
		const handlePriceInputChange = (e, fieldOnChange) => {
			// 숫자만 입력받도록 처리
			const numericValue = e.target.value.replace(/[^0-9]/g, '')

			// 천원 단위 콤마 적용
			const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

			// 필터링된 값으로 폼 업데이트
			fieldOnChange(formattedValue)
		}

		// 모달 열기 함수
		const handleModalOpen = () => {
			setIsModalOpen(true)
		}

		// 폼 제출 처리
		const onSubmit = (data) => {
			console.log('폼 데이터:', data)
			// 여기에 제출 로직 추가
		}

		// ref를 통해 부모 컴포넌트에서 호출할 수 있는 메서드들
		useImperativeHandle(ref, () => ({
			getFormData: () => {
				return {
					applyTax: activeData?.applyTax,
					payPeriod: 'oneTime',
					periodSalary: activeData?.periodSalary
						? activeData.periodSalary.replace(/,/g, '')
						: '',
					sponsorCap: activeData?.sponsorCap,
					overseasTour: activeData?.overseasTour,
					overseasSupport: activeData?.overseasSupport,
					incentiveSettings: activeData?.incentiveSettings,
					incentiveTaxOption: activeData?.incentiveTaxOption,
				}
			},
		}))

		return (
			<div className={styles.contractInfoSubmitFormWrapper}>
				<span className={styles.contractInfoSubmitFormTitle}>계약 정보를 작성해주세요</span>
				<form
					onSubmit={handleSubmit(onSubmit)}
					className={styles.contractInfoSubmitFormContainer}>
					<div className={styles.applyTax}>
						<ToggleButtonGroupController
							control={control}
							name="general.applyTax"
							toggleFieldLabel="임금 세금 포함 여부"
							toggleOption={applyTaxOption}
							isNoticeNeed={true}
							isNoticeNeedText={`세전 : 3.3% 제외 후 입금 예정\n세후 : 3.3% 제외된 실 입금 금액`}
							styleMode={styles}
						/>
					</div>
					<div className={styles.periodSalary}>
						<SimpleInputController
							control={control}
							name="general.periodSalary"
							rules={{ required: '기간급여액을 입력해주세요' }}
							inputLabel="기간급여액"
							inputPlaceholder="기간급여액을 입력해주세요"
							onChange={handlePriceInputChange}
						/>
					</div>
					<div className={styles.incentive}>
						<div className={styles.incentiveInfo}>
							인센티브
							<InformationCircleIcon
								className={styles.noticeIcon}
								data-tooltip-id="my-tooltip10"
								data-tooltip-content={`상금 표기액 : 각 투어별 홈페이지에 명시된 금액 \n 상금 실수령액 : KPGA 10%, KLPGA 9.3% 제외된 금액`}
							/>
							<Tooltip id="my-tooltip10" place="top" className={styles.tooltip} />
						</div>
						<button type="button" onClick={handleModalOpen}>
							입력하기
						</button>
					</div>

					<Controller
						name="general.incentiveSettings"
						control={control}
						render={({ field }) => <input type="hidden" {...field} />}
					/>
					{activeData?.incentiveSettings && activeData?.incentiveSettings.length > 0 && (
						<div className={styles.incentiveDisplaySection}>
							<div className={styles.incentiveTaxDisplay}>
								{formatIncentiveTaxOptionLabel(activeData.incentiveTaxOption)}
							</div>
							{activeData.incentiveSettings.map((setting) => {
								if (setting.percentage === null && setting.end === null) return null
								const displayRank =
									setting.id === 1
										? '우승 : '
										: setting.start +
											(setting.end !== null ? ` ~ ${setting.end} :  ` : '~ ')
								const displayPercentage =
									setting.percentage !== null
										? `  ${setting.percentage}%`
										: '미입력'
								return (
									<div key={setting.id} className={styles.incentiveDisplayItem}>
										<div>{displayRank}</div>

										<div>{displayPercentage}</div>
									</div>
								)
							})}
						</div>
					)}
					<div className={styles.overseasTour}>
						<ToggleButtonGroupController
							control={control}
							name="general.overseasTour"
							toggleFieldLabel="해외 대회 포함 여부"
							toggleOption={overseasTourOption}
							styleMode={styles}
							disabledOptions={isOverseas ? [false] : []}
						/>
					</div>
					<div className={styles.sponsorCap}>
						<ToggleButtonGroupController
							control={control}
							name="general.sponsorCap"
							toggleFieldLabel="선수 스폰서 모자 착용"
							toggleOption={sponsorCapOption}
							styleMode={styles}
						/>
					</div>
				</form>

				{/* 인센티브 모달 */}
				<IncentiveModal
					isModalOpen={isModalOpen}
					onFinish={(incentiveTaxOption, incentiveSettings) => {
						setValue('general.incentiveSettings', incentiveSettings)
						setValue('general.incentiveTaxOption', incentiveTaxOption)
						setIsModalOpen(false)
					}}
					onReturn={() => setIsModalOpen(false)}
					initialSettings={activeData?.incentiveSettings}
				/>
			</div>
		)
	},
)

export default GeneralContractInfoSubmitForm
